import axios from "axios";
import { API_BASE_URL } from "../../config/apiConfig";

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

apiClient.interceptors.request.use((config) => {
  const user = JSON.parse(localStorage.getItem("user"));
  if (user?.token) {
    config.headers.Authorization = `Bearer ${user.token}`;
  }
  return config;
});

export const qaApi = {
  /**
   * VOTE ON QUESTION
   * Endpoint: POST /api/posts/:id/vote
   * Body: { voteType: 'upvote' | 'downvote' }
   * Response: { action, score, upvotes, downvotes }
   */
  voteQuestion: async (postId, voteType) => {
    return apiClient.post(`/posts/${postId}/vote`, { voteType });
  },

  /**
   * POST ANSWER
   * Endpoint: POST /api/posts/:id/answers
   * Body: { content, code }
   * Response: { answer }
   */
  postAnswer: async (postId, answerData) => {
    // Ensure both content and code are always present
    const payload = {
      content: answerData.content || "",
      code: answerData.code || "",
    };
    return apiClient.post(`/posts/${postId}/answers`, payload);
  },

  /**
   * VOTE ON ANSWER
   * Endpoint: POST /api/posts/:postId/answers/:answerId/vote
   * Body: { voteType: 'upvote' | 'downvote' }
   */
  voteAnswer: async (postId, answerId, voteType) => {
    return apiClient.post(`/posts/${postId}/answers/${answerId}/vote`, {
      voteType,
    });
  },

  /**
   * ACCEPT ANSWER (Question author only)
   * Endpoint: POST /api/posts/:postId/answers/:answerId/accept
   * Response: { accepted, answerId }
   */
  acceptAnswer: async (postId, answerId) => {
    return apiClient.post(`/posts/${postId}/answers/${answerId}/accept`);
  },

  /**
   * ADD BOUNTY TO QUESTION
   * Endpoint: POST /api/posts/:id/bounty
   * Body: { amount }
   * Response: { bounty, userReputation }
   */
  addBounty: async (postId, amount) => {
    return apiClient.post(`/posts/${postId}/bounty`, { amount });
  },

  /**
   * AWARD BOUNTY TO ANSWER
   * Endpoint: POST /api/posts/:postId/answers/:answerId/award-bounty
   */
  awardBounty: async (postId, answerId) => {
    return apiClient.post(`/posts/${postId}/answers/${answerId}/award-bounty`);
  },

  /**
   * GET UNANSWERED QUESTIONS
   * Endpoint: GET /api/posts/questions/unanswered
   * Params: { limit, offset, sortBy: 'recent' | 'votes' | 'bounty' }
   */
  getUnansweredQuestions: async ({
    limit = 20,
    offset = 0,
    sortBy = "recent",
  } = {}) => {
    return apiClient.get("/posts/questions/unanswered", {
      params: { limit, offset, sortBy },
    });
  },

  /**
   * GET BOUNTY QUESTIONS
   * Endpoint: GET /api/posts/questions/bounty
   * Params: { limit, offset }
   */
  getBountyQuestions: async (params = {}) => {
    const { limit = 20, offset = 0 } = typeof params === "object" ? params : { limit: params };
    return apiClient.get("/posts/questions/bounty", {
      params: { limit, offset },
    });
  },

  /**
   * EDIT ANSWER
   * Endpoint: PUT /api/posts/:postId/answers/:answerId
   * Body: { content, code }
   */
  editAnswer: async (postId, answerId, { content, code }) => {
    return apiClient.put(`/posts/${postId}/answers/${answerId}`, {
      content,
      code,
    });
  },

  /**
   * DELETE ANSWER
   * Endpoint: DELETE /api/posts/:postId/answers/:answerId
   */
  deleteAnswer: async (postId, answerId) => {
    return apiClient.delete(`/posts/${postId}/answers/${answerId}`);
  },

  /**
   * GET USER REPUTATION
   * Endpoint: GET /api/users/:userId/reputation
   */
  getUserReputation: async (userId) => {
    return apiClient.get(`/users/${userId}/reputation`);
  },

  /**
   * GET ANSWERS FOR A QUESTION (Paginated & Cached)
   * Endpoint: GET /api/posts/:postId/answers
   * Params: { limit, offset, sortBy }
   * Response: { answers, pagination, cached }
   */
  getAnswers: async (
    postId,
    { limit = 10, offset = 0, sortBy = "accepted" } = {}
  ) => {
    return apiClient.get(`/posts/${postId}/answers`, {
      params: { limit, offset, sortBy },
    });
  },
};

export default qaApi;
